// TournamentRegistrationsSection — friendlier rendering of a user's tournament
// registrations, with cleaner status pills, role chips, fee breakdown and
// inline "Pay Now" for awaiting-payment team-owner registrations.
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

const STATUS_META = {
  approved:                  { label: 'Approved',         tone: 'bg-emerald-100 text-emerald-700', icon: 'ph-check-circle' },
  pending:                   { label: 'Pending review',   tone: 'bg-amber-100 text-amber-700',     icon: 'ph-clock' },
  rejected:                  { label: 'Rejected',         tone: 'bg-rose-100 text-rose-600',       icon: 'ph-x-circle' },
  approved_pending_payment:  { label: 'Awaiting payment', tone: 'bg-blue-100 text-blue-700',       icon: 'ph-credit-card' },
  draft:                     { label: 'Draft',            tone: 'bg-gray-100 text-gray-500',       icon: 'ph-pencil-simple' },
};

const ROLE_META = {
  player:     { label: 'Player',     tone: 'bg-blue-50 text-blue-700 border-blue-200',     icon: 'ph-person-simple-throw' },
  team_owner: { label: 'Team Owner', tone: 'bg-purple-50 text-purple-700 border-purple-200', icon: 'ph-crown-simple' },
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return '—'; }
};

function StatusPill({ status, testId }) {
  const meta = STATUS_META[status] || { label: status, tone: 'bg-gray-100 text-gray-600', icon: 'ph-info' };
  return (
    <span
      data-testid={testId}
      className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wide ${meta.tone}`}
    >
      <i className={`ph-bold ${meta.icon} text-[11px]`} />
      {meta.label}
    </span>
  );
}

function RoleChip({ role, testId }) {
  const meta = ROLE_META[role] || { label: role || 'Member', tone: 'bg-amber-50 text-amber-700 border-amber-200', icon: 'ph-user' };
  return (
    <span
      data-testid={testId}
      className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold border ${meta.tone}`}
    >
      <i className={`ph-bold ${meta.icon} text-[11px]`} />
      {meta.label}
    </span>
  );
}

function FeeBreakdown({ rd, fallbackTotal }) {
  const total = rd.final_total_fee || rd.calculated_total_fee || fallbackTotal || 0;
  const gst   = rd.final_gst_amount || rd.calculated_gst_amount || 0;
  const base  = rd.final_base_fee   || rd.calculated_base_fee   || (total - gst);
  return (
    <div className="mt-2 p-2.5 bg-blue-50/60 rounded-lg border border-blue-100 text-[11px] text-gray-700">
      <div className="flex items-center justify-between">
        <span>Base fee</span>
        <span className="font-medium">₹{(base / 100).toLocaleString('en-IN')}</span>
      </div>
      {gst > 0 && (
        <div className="flex items-center justify-between mt-0.5">
          <span>GST</span>
          <span className="font-medium">₹{(gst / 100).toLocaleString('en-IN')}</span>
        </div>
      )}
      <div className="flex items-center justify-between mt-1 pt-1 border-t border-blue-100">
        <span className="font-bold text-[#2B3A67]">Amount due</span>
        <span className="font-extrabold text-blue-700">₹{(total / 100).toLocaleString('en-IN')}</span>
      </div>
    </div>
  );
}

function RegistrationCard({ reg, idx, onPay, paying }) {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);
  const isAwaitingPayment = reg.status === 'approved_pending_payment';

  return (
    <div
      data-testid={`profile-registration-${idx}`}
      className={`rounded-2xl p-4 border transition-all ${
        isAwaitingPayment
          ? 'bg-blue-50/50 border-blue-200 shadow-sm'
          : 'bg-white border-gray-100 hover:border-[#3AAFA9]/40'
      }`}
    >
      <div className="flex items-start gap-3">
        <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${
          isAwaitingPayment ? 'bg-blue-100' : 'bg-gradient-to-br from-[#F4D341]/30 to-[#3AAFA9]/15'
        }`}>
          <i className={`ph-bold ph-trophy text-lg ${isAwaitingPayment ? 'text-blue-700' : 'text-[#2B3A67]'}`} />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <p className="font-bold text-sm text-[#2B3A67] truncate" data-testid={`profile-reg-event-${idx}`}>
              {reg.event_title || 'Tournament'}
            </p>
            <StatusPill status={reg.status} testId={`profile-reg-status-${idx}`} />
          </div>

          <div className="mt-2 flex items-center gap-2 flex-wrap">
            <RoleChip role={reg.role} testId={`profile-reg-role-${idx}`} />
            <span className="text-[10px] text-gray-400">
              <i className="ph ph-calendar-check mr-1" />
              {fmtDate(reg.created_at)}
            </span>
          </div>

          {isAwaitingPayment && reg.registration_data && (
            <FeeBreakdown rd={reg.registration_data} fallbackTotal={reg.payment_amount} />
          )}

          {expanded && reg.registration_data && (
            <div className="mt-3 p-3 bg-gray-50 rounded-lg text-[11px] text-gray-600 space-y-1">
              {Object.entries(reg.registration_data)
                .filter(([k, v]) => !k.startsWith('final_') && !k.startsWith('calculated_') && typeof v !== 'object')
                .slice(0, 6)
                .map(([k, v]) => (
                  <div key={k} className="flex justify-between gap-3">
                    <span className="capitalize text-gray-500">{k.replace(/_/g, ' ')}</span>
                    <span className="font-medium text-right truncate max-w-[60%]">{String(v)}</span>
                  </div>
                ))}
            </div>
          )}

          <div className="mt-3 flex items-center gap-2 flex-wrap">
            {isAwaitingPayment && reg.role === 'team_owner' && (
              <button
                onClick={() => onPay(reg)}
                disabled={paying}
                className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold disabled:opacity-50 disabled:cursor-not-allowed transition"
                data-testid={`profile-reg-pay-${idx}`}
              >
                {paying ? (
                  <>
                    <span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Processing…
                  </>
                ) : (
                  <>
                    <i className="ph-bold ph-credit-card" />
                    Pay now
                  </>
                )}
              </button>
            )}
            <button
              onClick={() => navigate(`/tournaments/${reg.event_id}`)}
              className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-[#2B3A67] hover:bg-[#1f2a4d] text-white text-xs font-bold transition"
              data-testid={`profile-reg-view-${idx}`}
            >
              <i className="ph-bold ph-eye" />
              View tournament
            </button>
            {reg.registration_data && (
              <button
                onClick={() => setExpanded((v) => !v)}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-gray-100 text-gray-700 text-xs font-bold hover:bg-gray-200 transition"
                data-testid={`profile-reg-details-${idx}`}
              >
                <i className={`ph-bold ${expanded ? 'ph-caret-up' : 'ph-caret-down'}`} />
                {expanded ? 'Hide' : 'Details'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function TournamentRegistrationsSection({
  registrations = [],
  payingRegistrationId = null,
  onPay,
}) {
  const navigate = useNavigate();
  const awaitingPayment = registrations.filter(r => r.status === 'approved_pending_payment').length;

  return (
    <section
      className="bg-white rounded-2xl p-5 shadow-md border border-gray-100"
      data-testid="profile-registrations"
    >
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="font-extrabold text-[#2B3A67]">Tournament Registrations</h3>
          <p className="text-[11px] text-gray-500 mt-0.5">
            {registrations.length === 0
              ? 'Sign up as a player or team owner to compete.'
              : `${registrations.length} registration${registrations.length !== 1 ? 's' : ''}`}
            {awaitingPayment > 0 && (
              <span className="ml-2 text-blue-700 font-bold">
                · {awaitingPayment} awaiting payment
              </span>
            )}
          </p>
        </div>
      </div>

      {registrations.length === 0 ? (
        <div
          className="text-center py-8 px-4 bg-gray-50 rounded-2xl border-2 border-dashed border-gray-200"
          data-testid="profile-registrations-empty"
        >
          <div className="w-12 h-12 mx-auto rounded-full bg-white flex items-center justify-center shadow-sm">
            <i className="ph-bold ph-trophy text-[#E54562] text-xl" />
          </div>
          <p className="mt-3 text-sm font-bold text-[#2B3A67]">No tournament registrations yet</p>
          <button
            onClick={() => navigate('/tournaments')}
            className="mt-3 px-4 py-2 rounded-full bg-[#2B3A67] text-white text-xs font-bold hover:bg-[#1f2a4d] transition"
          >
            Browse tournaments
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {registrations.map((reg, idx) => (
            <RegistrationCard
              key={reg.id}
              reg={reg}
              idx={idx}
              onPay={onPay}
              paying={payingRegistrationId === reg.id}
            />
          ))}
        </div>
      )}
    </section>
  );
}
